import React, { useState, useEffect } from 'react';
import { Database, Upload, Settings, Plus, Trash2, Save, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { collection, query, getDocs, doc, getDoc, writeBatch, setDoc, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const Assessment = () => {
  const { t } = useT();
  const [activeTab, setActiveTab] = useState('data');
  const { activeSchoolId, isMobile } = useOutletContext();
  const [academicYears, setAcademicYears] = useState([]);
  const [globalSchoolYear, setGlobalSchoolYear] = useState('');
  const [searchSid, setSearchSid] = useState('');
  
  const [importing, setImporting] = useState(false);
  const [assessmentsMap, setAssessmentsMap] = useState({});
  const [systemSubjects, setSystemSubjects] = useState([]);
  const [validSubjectCodes, setValidSubjectCodes] = useState([]);
  const [samsSubjConvDocs, setSamsSubjConvDocs] = useState([]);
  const [fullMarkDocs, setFullMarkDocs] = useState({});

  const [rawData, setRawData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);

  const [assessmentSettings, setAssessmentSettings] = useState({
    useRawGrade: true,
    gradeMapping: [],
    subjectMapping: []
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [gradeMappingExpanded, setGradeMappingExpanded] = useState(false);
  const [subjectMappingExpanded, setSubjectMappingExpanded] = useState(false);
  const [fullMarkExpanded, setFullMarkExpanded] = useState(false);

  const tabs = [
    { id: 'data', label: t('assessment.tabs.data'), icon: Database },
    { id: 'settings', label: t('assessment.tabs.settings'), icon: Settings }
  ];

  useEffect(() => {
    if (activeSchoolId) {
      fetchDropdownData();
    }
  }, [activeSchoolId]);

  useEffect(() => {
    if (!globalSchoolYear && academicYears.length > 0) {
      setGlobalSchoolYear(academicYears[0]);
    }
  }, [academicYears, globalSchoolYear]);

  useEffect(() => {
    if (activeSchoolId && globalSchoolYear) {
      if (searchSid) {
        fetchRawData(searchSid);
      } else {
        setRawData([]);
      }
    }
  }, [activeSchoolId, globalSchoolYear]);

  const fetchRawData = async (sidQuery = searchSid) => {
    if (!activeSchoolId || !globalSchoolYear) return;
    setLoadingData(true);
    try {
      let q = collection(db, 'schools', activeSchoolId, 'ass_raw_data');
      if (sidQuery) {
        // Query by SID if search string exists
        // Note: we still filter by SCHYEAR client-side below
        q = query(q, where('SID', '==', sidQuery.trim()));
      } else {
        q = query(q);
      }
      
      const snap = await getDocs(q);
      const list = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.SCHYEAR === globalSchoolYear) {
          list.push({ id: d.id, ...data });
        }
      });
      // Sort by asrec_id
      list.sort((a, b) => (a.asrec_id || '').localeCompare(b.asrec_id || ''));
      setRawData(list);
    } catch (e) {
      console.error(e);
    }
    setLoadingData(false);
  };

  const fetchDropdownData = async () => {
    if (!activeSchoolId) return;
    try {
      const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
      const yList = [];
      ySnap.forEach(d => {
        const data = d.data();
        yList.push(data.yearName || data.yearId);
      });
      setAcademicYears(yList.sort().reverse());

      // Fetch system settings for mapping
      const settingsSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        const dropdowns = data.dropdowns || {};
        
        // Build maps for SAMS Code -> Internal Code
        const aMap = {};
        (dropdowns.assessments || []).forEach(item => {
          if (item.samsCode) {
            aMap[item.samsCode] = item.code;
          }
        });
        setAssessmentsMap(aMap);

        const vCodes = [];
        const sysSubjs = dropdowns.subjects || [];
        sysSubjs.forEach(item => {
          if (item.code) vCodes.push(item.code);
        });
        setSystemSubjects(sysSubjs);
        setValidSubjectCodes(vCodes);
      }

      // Fetch conversion documents
      const convSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'sams_subj_conv'));
      const convDocs = [];
      convSnap.forEach(d => {
        convDocs.push({ id: d.id, ...d.data() });
      });
      setSamsSubjConvDocs(convDocs);

      // Fetch full marks
      const fmSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'subj_full_mark'));
      const fmMap = {};
      fmSnap.forEach(d => {
        const data = d.data();
        if (data.subjCode && data.fullMark !== undefined) {
          fmMap[data.subjCode] = data.fullMark;
        }
      });
      setFullMarkDocs(fmMap);

      // Fetch assessment settings
      const assSettingsSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'assessment_settings'));
      if (assSettingsSnap.exists()) {
        setAssessmentSettings(assSettingsSnap.data());
      } else {
        setAssessmentSettings({
          useRawGrade: true,
          gradeMapping: [],
          subjectMapping: []
        });
      }

    } catch (e) {
      console.error('Error fetching dropdown data', e);
    }
  };

  const getActiveInternalCodes = () => {
    const activeCodes = new Set();
    
    // Group systemSubjects by SAMS Code
    const groupedSys = {};
    systemSubjects.forEach(s => {
      if (s.samsCode) {
        if (!groupedSys[s.samsCode]) groupedSys[s.samsCode] = [];
        groupedSys[s.samsCode].push(s);
      }
    });

    Object.keys(groupedSys).forEach(samsCode => {
      const subjects = groupedSys[samsCode];
      if (subjects.length === 1) {
        activeCodes.add(subjects[0].code);
      } else {
        // Check for explicit standard override
        const explicitMatch = samsSubjConvDocs.find(c => c.samsCode === samsCode && c.isSystem);
        if (explicitMatch && explicitMatch.subjCode) {
          activeCodes.add(explicitMatch.subjCode);
        }
      }
    });

    // Add custom mappings
    samsSubjConvDocs.forEach(c => {
      if (!c.isSystem && c.subjCode) {
        activeCodes.add(c.subjCode);
      }
    });

    return Array.from(activeCodes).filter(Boolean).sort();
  };

  const handleSaveSettings = async () => {
    if (!activeSchoolId) return;
    setSavingSettings(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'assessment_settings'), assessmentSettings);

      // Save custom conversions to sams_subj_conv
      const batch = writeBatch(db);
      samsSubjConvDocs.forEach(docData => {
        if (docData.samsCode) {
          const ref = doc(db, 'schools', activeSchoolId, 'sams_subj_conv', docData.samsCode);
          batch.set(ref, docData);
        }
      });

      // Save full marks to subj_full_mark
      const activeCodes = getActiveInternalCodes();
      activeCodes.forEach(subjCode => {
        const fmValue = fullMarkDocs[subjCode] !== undefined && fullMarkDocs[subjCode] !== '' ? fullMarkDocs[subjCode] : 100;
        const ref = doc(db, 'schools', activeSchoolId, 'subj_full_mark', subjCode);
        batch.set(ref, { subjCode, fullMark: fmValue }, { merge: true });
      });
      await batch.commit();

      alert(t('assessment.settingsSaved'));
    } catch (e) {
      console.error('Error saving settings', e);
      alert(t('assessment.settingsSaveFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // Unified resolveCode
  const resolveCode = (samsCode) => {
    if (!samsCode) return samsCode;
    
    // 1. Check sams_subj_conv for explicitly set standard/custom conversion
    const explicitlySet = samsSubjConvDocs.find(c => c.samsCode === samsCode);
    if (explicitlySet && explicitlySet.subjCode) {
      return explicitlySet.subjCode;
    }

    // 2. Check System Parameters
    const sysMatches = systemSubjects.filter(s => s.samsCode === samsCode);
    if (sysMatches.length === 1) {
      return sysMatches[0].code;
    }

    // If ambiguous (>1) or not found (0), and no explicit conversion set, do not convert
    return samsCode;
  };

  const handleFindUnconverted = () => {
    // Collect all unique CODE_IDs currently in raw data
    const uniqueCodes = new Set();
    rawData.forEach(item => {
      if (item.CODE_ID) uniqueCodes.add(item.CODE_ID);
    });

    // Known valid internal subject codes
    const validCodesSet = new Set(validSubjectCodes);
    
    // Existing mapped SAMS codes in custom mapping
    const existingMappedSams = new Set(samsSubjConvDocs.filter(c => !c.isSystem).map(m => m.samsCode));

    const unconverted = [];
    uniqueCodes.forEach(code => {
      // It's unconverted if it's not a valid internal code and not already in our custom mapping list
      // Also, it must not be resolved to a valid code by resolveCode
      const resolved = resolveCode(code);
      if (resolved === code && !validCodesSet.has(code) && !existingMappedSams.has(code)) {
        unconverted.push(code);
      }
    });

    if (unconverted.length === 0) {
      alert(t('assessment.noUnconverted'));
      return;
    }

    const newRows = unconverted.map(code => ({ samsCode: code, subjCode: '', isSystem: false }));
    setSamsSubjConvDocs([...samsSubjConvDocs, ...newRows]);
    alert(t('assessment.foundUnconverted', { count: unconverted.length }));
  };

  const handleConvertSamsToSubj = async () => {
    if (!activeSchoolId) return;
    const isConfirmed = window.confirm(t('assessment.convertConfirm'));
    if (!isConfirmed) return;

    try {
      const q = query(collection(db, 'schools', activeSchoolId, 'ass_raw_data'));
      const snap = await getDocs(q);

      let batch = writeBatch(db);
      let count = 0;
      let updatedCount = 0;

      snap.forEach((d) => {
        const data = d.data();
        if (data.SCHYEAR === globalSchoolYear) {
          const currentCodeId = data.CODE_ID;
          const targetCode = resolveCode(currentCodeId);
          if (targetCode && targetCode !== currentCodeId && validSubjectCodes.includes(targetCode)) {
            batch.update(d.ref, { CODE_ID: targetCode });
            updatedCount++;
            count++;
          }
        }
        if (count === 490) { // Keep under 500
          batch.commit();
          batch = writeBatch(db);
          count = 0;
        }
      });

      if (count > 0) {
        await batch.commit();
      }

      alert(t('assessment.converted', { count: updatedCount }));
      fetchRawData(); // Refresh data view
    } catch (e) {
      console.error(e);
      alert(t('assessment.convertFailed', { message: e.message }));
    }
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target.result;
      const arr = new Uint8Array(buffer);
      let encoding = 'utf-8';
      try {
        new TextDecoder('utf-8', { fatal: true }).decode(arr);
      } catch (err) {
        encoding = 'Big5';
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: encoding,
        complete: async (results) => {
          try {
            const parsedData = results.data;
            if (parsedData.length === 0) {
              alert(t('assessment.csvNoData'));
              setImporting(false);
              return;
            }

            // Create batches (max 500 ops per batch)
            const batches = [];
            let currentBatch = writeBatch(db);
            let operationCount = 0;

            parsedData.forEach(row => {
              // Extract and map data
              const rawYear = String(row['SCHYEAR'] || '').trim();
              let formattedYear = rawYear;
              if (rawYear && /^\d{4}$/.test(rawYear)) {
                formattedYear = `${rawYear}-${parseInt(rawYear) + 1}`;
              }

              const rawTimeseq = String(row['TIMESEQ'] || '').trim();
              const mappedTimeseq = assessmentsMap[rawTimeseq] || rawTimeseq;

              const rawCodeId = String(row['CODE_ID'] || '').trim();
              const mappedCodeId = resolveCode(rawCodeId) || rawCodeId;

              const sid = String(row['SID'] || '').trim();
              const mark = String(row['Mark'] || '').trim();
              const omf = String(row['OMF'] || '').trim();
              const grade = String(row['Grade'] || '').trim();

              if (!formattedYear || !mappedTimeseq || !mappedCodeId || !sid) {
                // Skip invalid rows
                return;
              }

              const asrecId = `${formattedYear}_${mappedTimeseq}_${mappedCodeId}_${sid}`;
              const docRef = doc(db, 'schools', activeSchoolId, 'ass_raw_data', asrecId);
              
              currentBatch.set(docRef, {
                asrec_id: asrecId,
                SCHYEAR: formattedYear,
                TIMESEQ: mappedTimeseq,
                CODE_ID: mappedCodeId,
                SID: sid,
                Mark: mark,
                OMF: omf,
                Grade: grade,
                created_at: new Date().toISOString(),
                created_by: auth.currentUser?.email || 'Unknown',
                last_updated_at: new Date().toISOString(),
                last_updated_by: auth.currentUser?.email || 'Unknown'
              }, { merge: true });

              operationCount++;

              if (operationCount === 500) {
                batches.push(currentBatch);
                currentBatch = writeBatch(db);
                operationCount = 0;
              }
            });

            if (operationCount > 0) {
              batches.push(currentBatch);
            }

            for (const batch of batches) {
              await batch.commit();
            }

            alert(t('assessment.imported', { count: batches.length }));
            fetchRawData();
          } catch (err) {
            console.error('Import error', err);
            alert(t('assessment.importFailed', { message: err.message }));
          } finally {
            setImporting(false);
          }
        },
        error: (err) => {
          alert(t('assessment.parseFailed', { message: err.message }));
          setImporting(false);
        }
      });
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null; // reset input
  };

  const handleExportCSV = async () => {
    if (!activeSchoolId || !globalSchoolYear) {
      alert(t('assessment.selectYearFirst'));
      return;
    }

    setImporting(true); // Re-using importing state for loading overlay

    try {
      // Query raw data for the selected school year
      // Note: we can filter by SCHYEAR if we create an index, or we just fetch and filter.
      // Assuming a composite index might not exist, we fetch all and filter client side to be safe,
      // or use a query. We'll use a basic query, which only requires a single-field index (default).
      const rawRef = collection(db, 'schools', activeSchoolId, 'ass_raw_data');
      // getDocs without query if we want to be safe, but where('SCHYEAR', '==', globalSchoolYear) works if indexed.
      // Wait, Firestore automatically indexes single fields, so this is fine.
      const q = query(rawRef);
      const snap = await getDocs(q);
      
      const csvData = [];
      snap.forEach(docSnap => {
        const d = docSnap.data();
        if (d.SCHYEAR === globalSchoolYear) {
          csvData.push({
            'SCHYEAR': d.SCHYEAR || '',
            'TIMESEQ': d.TIMESEQ || '',
            'CODE_ID': d.CODE_ID || '',
            'SID': d.SID || '',
            'Mark': d.Mark || '',
            'OMF': d.OMF || '',
            'Grade': d.Grade || ''
          });
        }
      });

      // If no data, provide a sample row
      if (csvData.length === 0) {
        csvData.push({
          'SCHYEAR': globalSchoolYear.split('-')[0], // e.g., 2019
          'TIMESEQ': '1101',
          'CODE_ID': '080',
          'SID': 'S123456',
          'Mark': '85',
          'OMF': '90',
          'Grade': 'A'
        });
        alert(t('assessment.exportingTemplate'));
      }

      const csv = Papa.unparse(csvData);
      const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' }); // BOM
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Assessment_Data_${globalSchoolYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (e) {
      console.error('Export error', e);
      alert(t('assessment.exportFailed', { message: e.message }));
    } finally {
      setImporting(false);
    }
  };

  const totalPages = Math.ceil(rawData.length / recordsPerPage) || 1;
  const paginatedData = rawData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  return (
    <>
      {importing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          zIndex: 99999, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'var(--color-bg-card)', padding: '1.5rem 3rem', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--color-border)' }}>
            <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
            </svg>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--color-text)' }}>
              Processing... Please wait.
            </span>
          </div>
        </div>
      )}
      <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Tabs */}
        <div className="theme-tabs-bar">
          <div className="theme-tabs-group">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`theme-tab ${isActive ? 'active' : ''}`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <div className="theme-tab-icon">
                    <Icon size={18} />
                  </div>
                  {tab.label}
                </button>
              )
            })}
          </div>
          
          <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {activeTab === 'data' && (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder={t('assessment.searchSid')}
                  value={searchSid}
                  onChange={(e) => setSearchSid(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchRawData(searchSid);
                    }
                  }}
                  style={{ width: '150px', padding: '0.4rem 2rem 0.4rem 0.75rem', height: '36px' }}
                />
                <button
                  onClick={() => fetchRawData(searchSid)}
                  style={{ position: 'absolute', right: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}
                  title={t('common.search')}
                >
                  <Search size={16} />
                </button>
              </div>
            )}

            {activeTab === 'data' && (
              <>
                <button onClick={handleExportCSV} title={t('assessment.exportCsv')} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: 0, padding: '0', width: '36px', height: '36px' }}>
                  <Upload size={18} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <label title={t('assessment.importCsv')} className="btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: 0, padding: '0', width: '36px', height: '36px' }}>
                  <Upload size={18} />
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
                </label>
              </>
            )}
            <select
              className="input-field"
              value={globalSchoolYear}
              onChange={e => setGlobalSchoolYear(e.target.value)}
              style={{ width: '130px', padding: '0.4rem 0.75rem', height: '36px', marginLeft: '0.5rem' }}
            >
              {academicYears.map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
          </HeaderActionsPortal>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: '1rem', padding: '0' }}>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
            {activeTab === 'data' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }}>

                <div style={{ padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                      {t('assessment.showingRecords', {
                        from: rawData.length > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0,
                        to: Math.min(currentPage * recordsPerPage, rawData.length),
                        total: rawData.length,
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{t('assessment.perPage')}</span>
                      <select className="input-field" value={recordsPerPage} onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                    <button className="btn-outline" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>{t('assessment.prev')}</button>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem' }}>{t('assessment.pageOf', { current: currentPage, total: totalPages })}</div>
                    <button className="btn-outline" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages || rawData.length === 0} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>{t('assessment.next')}</button>
                  </div>
                </div>

                <div className="table-container" style={{ flex: 1, backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>ASREC ID</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>TIMESEQ</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>CODE_ID</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>SID</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Mark</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>OMF</th>
                        <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingData ? <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</td></tr> :
                       rawData.length === 0 ? <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('assessment.noRecords')}</td></tr> :
                       paginatedData.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{r.asrec_id}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>{r.TIMESEQ}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>{r.CODE_ID}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{r.SID}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{r.Mark}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{r.OMF}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                            <span style={{ padding: '0.2rem 0.5rem', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '4px', fontWeight: '600' }}>
                              {r.Grade || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            ) : activeTab === 'settings' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1rem' }}>
                    <Save size={16} /> {savingSettings ? t('common.saving') : t('assessment.saveSettings')}
                  </button>
                </div>

                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', border: '1px solid var(--color-border)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: gradeMappingExpanded ? '1rem' : '0' }}
                    onClick={() => setGradeMappingExpanded(!gradeMappingExpanded)}
                  >
                    {gradeMappingExpanded ? <ChevronDown size={20} style={{ marginRight: '0.5rem' }} /> : <ChevronRight size={20} style={{ marginRight: '0.5rem' }} />}
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{t('assessment.gradeMapping')}</h3>
                  </div>
                  
                  {gradeMappingExpanded && (
                    <>
                      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>{t('assessment.gradeStrategy')}</label>
                        <select 
                          className="input-field" 
                          value={assessmentSettings.useRawGrade ? 'raw' : 'recalculate'}
                          onChange={(e) => setAssessmentSettings({...assessmentSettings, useRawGrade: e.target.value === 'raw'})}
                          style={{ width: '300px' }}
                        >
                          <option value="raw">{t('assessment.useRawGrade')}</option>
                          <option value="recalculate">{t('assessment.recalculateGrade')}</option>
                        </select>
                      </div>

                      <div className="table-container" style={{ backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                          <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
                            <tr>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.rank')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>{t('assessment.fail')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.refMark')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.grade1')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.grade2')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.grade3')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', width: '60px', textAlign: 'center' }}>{t('assessment.action')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(!assessmentSettings.gradeMapping || assessmentSettings.gradeMapping.length === 0) ? (
                              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('assessment.noGradeRows')}</td></tr>
                            ) : assessmentSettings.gradeMapping.map((row, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                <td style={{ padding: '0.5rem 1rem' }}>
                                  <input type="number" className="input-field" value={row.rank ?? ''} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].rank = Number(e.target.value);
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '80px' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                                  <input type="checkbox" checked={row.fail ?? false} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].fail = e.target.checked;
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem' }}>
                                  <input type="number" className="input-field" value={row.ref_mark ?? ''} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].ref_mark = Number(e.target.value);
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '100px' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem' }}>
                                  <input type="text" className="input-field" value={row.grade_1 || ''} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].grade_1 = e.target.value;
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '100px' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem' }}>
                                  <input type="text" className="input-field" value={row.grade_2 || ''} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].grade_2 = e.target.value;
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '100px' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem' }}>
                                  <input type="text" className="input-field" value={row.grade_3 || ''} onChange={(e) => {
                                    const newMapping = [...assessmentSettings.gradeMapping];
                                    newMapping[index].grade_3 = e.target.value;
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ width: '100px' }} />
                                </td>
                                <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                                  <button onClick={() => {
                                    const newMapping = assessmentSettings.gradeMapping.filter((_, i) => i !== index);
                                    setAssessmentSettings({...assessmentSettings, gradeMapping: newMapping});
                                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)' }}>
                                    <Trash2 size={18} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <button onClick={() => {
                        const sortedMapping = [...(assessmentSettings.gradeMapping || [])].sort((a,b) => (a.rank || 0) - (b.rank || 0));
                        const maxRank = sortedMapping.length > 0 ? (sortedMapping[sortedMapping.length-1].rank || 0) : 0;
                        setAssessmentSettings({
                          ...assessmentSettings,
                          gradeMapping: [
                            ...(assessmentSettings.gradeMapping || []),
                            { rank: maxRank + 1, fail: false, ref_mark: 0, grade_1: '', grade_2: '', grade_3: '' }
                          ]
                        });
                      }} className="btn-outline" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Plus size={16} /> {t('assessment.addGradeRow')}
                      </button>
                    </>
                  )}
                </div>

                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', border: '1px solid var(--color-border)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: subjectMappingExpanded ? '1rem' : '0' }}
                    onClick={() => setSubjectMappingExpanded(!subjectMappingExpanded)}
                  >
                    {subjectMappingExpanded ? <ChevronDown size={20} style={{ marginRight: '0.5rem' }} /> : <ChevronRight size={20} style={{ marginRight: '0.5rem' }} />}
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{t('assessment.subjectCodeMapping')}</h3>
                  </div>
                  
                  {subjectMappingExpanded && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <button onClick={handleFindUnconverted} className="btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                          {t('assessment.findUnconverted')}
                        </button>
                        <button onClick={handleConvertSamsToSubj} className="btn-primary" style={{ padding: '0.5rem 1rem' }}>
                          {t('assessment.convertSamsToSubj')}
                        </button>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('assessment.customSubjectMapping')}</h4>
                        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>{t('assessment.customSubjectMappingIntro')}</p>
                        <div className="table-container" style={{ backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
                              <tr>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.samsCodeFromCsv')}</th>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.subjectCodeInternal')}</th>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', width: '60px', textAlign: 'center' }}>{t('assessment.action')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {samsSubjConvDocs.filter(c => !c.isSystem).length === 0 ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('assessment.noCustomMappings')}</td></tr>
                              ) : samsSubjConvDocs.filter(c => !c.isSystem).map((row, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                  <td style={{ padding: '0.5rem 1rem' }}>
                                    <input type="text" className="input-field" value={row.samsCode || ''} onChange={(e) => {
                                      const newDocs = [...samsSubjConvDocs];
                                      const idx = newDocs.findIndex(d => d === row);
                                      if (idx > -1) {
                                        newDocs[idx].samsCode = e.target.value;
                                        setSamsSubjConvDocs(newDocs);
                                      }
                                    }} style={{ width: '200px' }} placeholder={t('assessment.samsCodePlaceholder')} />
                                  </td>
                                  <td style={{ padding: '0.5rem 1rem' }}>
                                    <input type="text" className="input-field" value={row.subjCode || ''} onChange={(e) => {
                                      const newDocs = [...samsSubjConvDocs];
                                      const idx = newDocs.findIndex(d => d === row);
                                      if (idx > -1) {
                                        newDocs[idx].subjCode = e.target.value;
                                        setSamsSubjConvDocs(newDocs);
                                      }
                                    }} style={{ width: '200px' }} placeholder={t('assessment.subjCodePlaceholder')} />
                                  </td>
                                  <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                                    <button onClick={() => {
                                      setSamsSubjConvDocs(samsSubjConvDocs.filter(d => d !== row));
                                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)' }}>
                                      <Trash2 size={18} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button onClick={() => {
                          setSamsSubjConvDocs([...samsSubjConvDocs, { samsCode: '', subjCode: '', isSystem: false }]);
                        }} className="btn-outline" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Plus size={16} /> {t('assessment.addCustomMapping')}
                        </button>
                      </div>

                      <div style={{ marginTop: '1rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('assessment.systemParameters')}</h4>
                        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>{t('assessment.systemParametersIntro')}</p>
                        <div className="table-container" style={{ backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', maxHeight: '300px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead style={{ backgroundColor: 'var(--color-bg-app)', position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.samsCode')}</th>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.subjectCode')}</th>
                                <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.standardTarget')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {systemSubjects.length === 0 ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('assessment.noSystemParameters')}</td></tr>
                              ) : (() => {
                                // Group systemSubjects by SAMS Code
                                const grouped = {};
                                systemSubjects.forEach(s => {
                                  if (s.samsCode) {
                                    if (!grouped[s.samsCode]) grouped[s.samsCode] = [];
                                    grouped[s.samsCode].push(s);
                                  }
                                });

                                return Object.keys(grouped).sort().map(samsCode => {
                                  const subjects = grouped[samsCode];
                                  return subjects.map((subj, idx) => {
                                    const explicitMatch = samsSubjConvDocs.find(c => c.samsCode === samsCode && c.isSystem);
                                    const isStandard = explicitMatch ? explicitMatch.subjCode === subj.code : subjects.length === 1;

                                    return (
                                      <tr key={`${samsCode}-${subj.code}`} style={{ borderBottom: idx === subjects.length - 1 ? '1px solid var(--color-border)' : 'none', backgroundColor: subjects.length > 1 ? 'rgba(0,0,0,0.01)' : 'transparent' }}>
                                        {idx === 0 && (
                                          <td rowSpan={subjects.length} style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top', fontWeight: 'bold' }}>{samsCode}</td>
                                        )}
                                        <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{subj.code} ({subj.nameEn || subj.name})</td>
                                        <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>
                                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                            <input 
                                              type="radio" 
                                              name={`standard-${samsCode}`} 
                                              checked={isStandard} 
                                              onChange={() => {
                                                const newDocs = samsSubjConvDocs.filter(c => !(c.samsCode === samsCode && c.isSystem));
                                                newDocs.push({ samsCode, subjCode: subj.code, isSystem: true });
                                                setSamsSubjConvDocs(newDocs);
                                              }}
                                              disabled={subjects.length === 1}
                                            />
                                            {subjects.length === 1 ? 'Default (Only Option)' : 'Set Standard'}
                                          </label>
                                        </td>
                                      </tr>
                                    );
                                  });
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Full Mark Setting */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', border: '1px solid var(--color-border)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: fullMarkExpanded ? '1rem' : '0' }}
                    onClick={() => setFullMarkExpanded(!fullMarkExpanded)}
                  >
                    {fullMarkExpanded ? <ChevronDown size={20} style={{ marginRight: '0.5rem' }} /> : <ChevronRight size={20} style={{ marginRight: '0.5rem' }} />}
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{t('assessment.fullMarkSetting')}</h3>
                  </div>
                  
                  {fullMarkExpanded && (
                    <>
                      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>{t('assessment.fullMarkIntro')}</p>
                      <div className="table-container" style={{ backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                          <thead style={{ backgroundColor: 'var(--color-bg-app)', position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.subjectCode')}</th>
                              <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('assessment.fullMark')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const uniqueInternalCodes = getActiveInternalCodes();

                              if (uniqueInternalCodes.length === 0) {
                                return <tr><td colSpan="2" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('assessment.noSubjectCodes')}</td></tr>;
                              }

                              return uniqueInternalCodes.map(subjCode => (
                                <tr key={subjCode} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                  <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>{subjCode}</td>
                                  <td style={{ padding: '0.5rem 1rem' }}>
                                    <input 
                                      type="number" 
                                      className="input-field" 
                                      value={fullMarkDocs[subjCode] !== undefined ? fullMarkDocs[subjCode] : 100} 
                                      onChange={(e) => setFullMarkDocs({ ...fullMarkDocs, [subjCode]: e.target.value !== '' ? Number(e.target.value) : '' })}
                                      style={{ width: '120px' }} 
                                      placeholder={t('assessment.fullMarkPlaceholder')} 
                                      min="0"
                                    />
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'capitalize' }}>
                  {activeTab}
                </h2>
                <p style={{ color: 'var(--color-text-muted)' }}>Content for {activeTab} will go here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Assessment;
